// src/pages/Calendar.js
// NTRG deadline calendar — Phase 5 of the deadline-management build.
//
// Default view: list (sortable table of upcoming appeal deadlines).
// Toggle: month grid view.
// Filters: state, client, consultant, urgency window, and "Show completed".
//
// One row per parcel, for the parcel's LATEST NOTICE YEAR (utils/deadlineCalendar — the same rule the notice scan
// uses to decide whether it may write the base row). Rules decided by Philip 2026-10-14, docs/calendar-investigation.md:
//   • Dates: that year's notice rows (parcel_values) first — the earliest deadline across its jurisdictions — then the
//     base parcels row (notice_date / appeal_deadline). No deadline anywhere → computed from the notice date by the
//     state rule (shared deadlineRules utility).
//   • Done: the year's appeal status, levels, filed dates and decisions (satisfiedLabel). A done row is grey and hidden
//     until "Show completed"; urgency colours are for outstanding rows only.
//   • Each row's date is its filing deadline. Hearing and evidence-due dates are not shown: appeals are run by
//     consultants and NTRG doesn't track them (Philip, 2026-10-21).
//   • Consultant: the year's assignment, then the property's consultant_firm, then the parcel's old text. The filter
//     uses the same value.
// Appeal deadlines only; a future writable calendar-events table (meetings etc.) would need to be built — the old
// empty jurisdiction_calendar_events was dropped 2026-08-08 (deny-all + unused).
//
// SCHEMA NOTES:
//   parcels has NO jurisdiction column, so a computed deadline uses the statewide rule; per-county routing for
//     CA/NJ/NY only works when DocumentScanner extracts the jurisdiction from a notice. A known limitation.
//   clients has id (text), name, short_name.

import React, { useState, useEffect, useMemo } from 'react';
import ScrollX from '../components/ScrollX';
import { supabase } from '../supabase';
import { ORIGINAL_NOTICE } from '../utils/notices';
import { isParcelRepresented, PROPERTY_REPRESENTATION_COLS } from '../reports/represented';
import { fetchAll, fetchByParcelIds } from '../reports/pagination';
import { resolveFirmAssignmentsBulk } from '../utils/resolveAssignment';
import { latestNoticeYear, yearNotice, satisfiedLabel, calendarConsultant } from '../utils/deadlineCalendar';
import {
  loadDeadlineRules,
  calcAppealDeadline,
  urgencyTier,
  URGENCY_COLORS,
  URGENCY_COLORS_DARK,
} from '../utils/deadlineRules';

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_HEADERS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const URGENCY_WINDOWS = [
  { id: 'all',  label: 'All' },
  { id: '7',    label: 'Next 7 days' },
  { id: '30',   label: 'Next 30 days' },
  { id: '90',   label: 'Next 90 days' },
  { id: 'past', label: 'Past due only' },
];

// A done row: grey, whatever its date.
const DONE_COLORS = { bg: '#eef0f4', fg: '#8a90a2', border: '#d9dce4' };
const DONE_COLORS_DARK = { bg: '#2f3850', fg: '#8a90a8', border: '#3a4566' };

// Local-time YYYY-MM-DD without UTC roundtrip
function todayLocalIso() {
  const t = new Date();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${t.getFullYear()}-${m}-${d}`;
}

function addDaysIso(iso, n) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Group rows by parcel_id.
function byParcel(rows) {
  const m = new Map();
  (rows || []).forEach(r => { if (!m.has(r.parcel_id)) m.set(r.parcel_id, []); m.get(r.parcel_id).push(r); });
  return m;
}

export default function Calendar({ darkMode, staffUser }) {
  const isDark = !!darkMode;
  const C = isDark ? URGENCY_COLORS_DARK : URGENCY_COLORS;
  const DONE = isDark ? DONE_COLORS_DARK : DONE_COLORS;

  // ── State ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);      // one per calendar parcel: the parcel + its year, notice, done state, appeals, consultant
  const [clients, setClients] = useState([]);

  // View + filters
  const [view, setView] = useState('list');
  const [monthOffset, setMonthOffset] = useState(0);
  const [filterState, setFilterState] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterConsultantFirm, setFilterConsultantFirm] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortKey, setSortKey] = useState('deadline');
  const [sortDir, setSortDir] = useState('asc');

  // ── Load on mount ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load deadline rules into shared cache
        await loadDeadlineRules();

        // Every read pages past PostgREST's silent 1000-row cap, ordered so the pages don't overlap.
        const [pData, noticeData, propRows, { data: cData, error: cErr }] = await Promise.all([
          fetchAll('parcels', `
            id, account_number, state, address, city,
            notice_date, appeal_deadline,
            client_id, client_name, consultant_firm,
            parcel_name, property_id, first_year, retired_year, reactivated_year
          `, q => q.order('id')),
          fetchAll('parcel_values', 'id, parcel_id, tax_year, jurisdiction, notice_date, appeal_deadline',
            q => q.not('notice_date', 'is', null).eq('notice_no', ORIGINAL_NOTICE).order('id')),
          // ⛔ THE PROPERTY HALF OF THE CASCADE. A live parcel under a RETIRED property is not represented
          // (reports/represented.js), and gating on the parcel alone would be a half-gate that looks like a fix.
          // The same rows carry the property's consultant_firm, the consultant's second source.
          fetchAll('properties', `${PROPERTY_REPRESENTATION_COLS}, consultant_firm`, q => q.order('id')),
          supabase.from('clients').select('id, name, short_name').order('name', { ascending: true }),
        ]);
        if (cErr) console.warn('[Calendar] clients fetch error (non-fatal):', cErr);
        const propRep = new Map(propRows.map(pr => [pr.id, pr]));
        const noticesByParcel = byParcel(noticeData);

        // Each parcel's latest notice year and that year's dates.
        const candidates = [];
        for (const p of pData) {
          const pRows = noticesByParcel.get(p.id) || [];
          const year = latestNoticeYear(pRows, p.notice_date);
          if (year == null) continue;
          // ⛔ A DEADLINE FOR A YEAR WE DID NOT HOLD THE PARCEL IS NOT WORK. Gate on the NOTICE's year (the value
          // year it is for), not the deadline's: a notice dated 2025-12-04 can carry a January 2026 deadline.
          if (!isParcelRepresented(p, propRep, String(year))) continue;
          const yn = yearNotice(pRows.filter(r => Number(String(r.tax_year).slice(0, 4)) === year));
          // The year's notice rows first; the base row only when the year has none. The base holds the latest
          // notice too, so it only differs from the rows when it came from somewhere other than a scan.
          const notice = yn || { notice_date: p.notice_date, appeal_deadline: p.appeal_deadline, jurisdiction: null };
          if (!notice.notice_date && !notice.appeal_deadline) continue;
          candidates.push({ p, year, notice, fromYearRow: !!yn });
        }

        // The year's appeal status, levels, decisions and consultant — one batch per distinct year (few).
        const ids = (y) => candidates.filter(c => c.year === y).map(c => c.p.id);
        const years = [...new Set(candidates.map(c => c.year))];
        const perYear = new Map(await Promise.all(years.map(async (y) => {
          const ty = String(y);
          const [statuses, appeals, decisions, assignments] = await Promise.all([
            fetchByParcelIds('parcel_year_data', 'id, parcel_id, appeal_status', ids(y), q => q.eq('tax_year', ty).order('id')),
            fetchByParcelIds('parcel_appeals', 'id, parcel_id, appeal_level, filed_date', ids(y), q => q.eq('tax_year', ty).eq('notice_no', ORIGINAL_NOTICE).order('id')),
            fetchByParcelIds('parcel_appeal_decisions', 'id, parcel_id, appeal_level, decision', ids(y), q => q.eq('tax_year', ty).eq('notice_no', ORIGINAL_NOTICE).order('id')),
            resolveFirmAssignmentsBulk(ids(y), y),
          ]);
          return [y, {
            status: new Map(statuses.map(r => [r.parcel_id, r.appeal_status])),
            appeals: byParcel(appeals), decisions: byParcel(decisions), assignments,
          }];
        })));

        const built = candidates.map(({ p, year, notice, fromYearRow }) => {
          const yd = perYear.get(year);
          const appeals = yd.appeals.get(p.id) || [];
          return {
            parcel_id: p.id,
            account_number: p.account_number,
            state: p.state,
            address: p.address,
            city: p.city,
            parcel_name: p.parcel_name,
            client_id: p.client_id,
            client_name_from_parcel: p.client_name, // denormalized on parcel row
            consultant: calendarConsultant({
              assignment: yd.assignments.get(p.id),
              propertyFirm: p.property_id ? propRep.get(p.property_id)?.consultant_firm : null,
              parcelFirm: p.consultant_firm,
            }),
            year,
            notice_date: notice.notice_date,
            // Calendar passes no jurisdiction to the rule; statewide rule applies (see SCHEMA NOTES at top of file).
            filing_deadline: notice.appeal_deadline || (notice.notice_date ? calcAppealDeadline(p.state, notice.notice_date, null) : null),
            date_source: fromYearRow ? `${year} notice${notice.jurisdiction ? ` (${notice.jurisdiction})` : ''}` : 'parcel record',
            done: satisfiedLabel({ status: yd.status.get(p.id) ?? null, appeals, decisions: yd.decisions.get(p.id) || [] }),
            appeals,
          };
        });

        if (cancelled) return;
        setRows(built);
        setClients(cData || []);
        setLoading(false);
      } catch (e) {
        console.error('[Calendar] load error:', e);
        if (!cancelled) {
          setError(e.message || 'Failed to load calendar data');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Each row's deadline: its filing deadline ───────────────────────
  const events = useMemo(() => {
    const list = [];
    for (const r of rows) if (r.filing_deadline) list.push({ ...r, deadline: r.filing_deadline, what: 'Filing deadline' });
    return list;
  }, [rows]);

  // ── Lookups (built once) ───────────────────────────────────────────
  const clientById = useMemo(() => {
    const m = new Map();
    clients.forEach(c => m.set(c.id, c));
    return m;
  }, [clients]);

  const uniqueStates = useMemo(() => {
    const s = new Set();
    events.forEach(e => e.state && s.add(e.state));
    return [...s].sort();
  }, [events]);

  const consultantFirms = useMemo(() => {
    const s = new Set();
    events.forEach(e => e.consultant && s.add(e.consultant));
    return [...s].sort();
  }, [events]);

  const doneCount = useMemo(() => events.filter(e => e.done).length, [events]);

  // ── Apply filters ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const todayIso = todayLocalIso();
    return events.filter(e => {
      if (e.done && !showCompleted) return false;
      if (filterState && e.state !== filterState) return false;
      if (filterClient && e.client_id !== filterClient) return false;
      if (filterConsultantFirm && e.consultant !== filterConsultantFirm) return false;
      if (filterUrgency === 'past') {
        if (e.deadline >= todayIso) return false;
      } else if (filterUrgency !== 'all') {
        const days = parseInt(filterUrgency, 10);
        if (e.deadline < todayIso) return false;
        if (e.deadline > addDaysIso(todayIso, days)) return false;
      }
      return true;
    });
  }, [events, filterState, filterClient, filterConsultantFirm, filterUrgency, showCompleted]);

  // ── Sort — special-cased client to sort by name, not id ────────────
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === 'client') {
        av = (clientById.get(a.client_id)?.name || a.client_name_from_parcel || '').toLowerCase();
        bv = (clientById.get(b.client_id)?.name || b.client_name_from_parcel || '').toLowerCase();
      } else if (sortKey === 'consultant') {
        av = (a.consultant || '').toLowerCase();
        bv = (b.consultant || '').toLowerCase();
      } else {
        av = a[sortKey] ?? '';
        bv = b[sortKey] ?? '';
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir, clientById]);

  // ── Style tokens ───────────────────────────────────────────────────
  const bg = isDark ? 'var(--ink)' : 'var(--bg)';
  const panelBg = isDark ? '#2a3454' : '#ffffff';
  const border = isDark ? '#3a4566' : 'var(--line)';
  const text = isDark ? '#e8eaf2' : 'var(--ink)';
  const muted = isDark ? '#a8aec5' : 'var(--slate)';
  const accent = 'var(--red)';

  // ── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, color: muted, fontFamily: "'Public Sans',system-ui,sans-serif", fontSize: 14 }}>
        Loading calendar...
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, color: '#B40000', fontFamily: "'Public Sans',system-ui,sans-serif", fontSize: 14 }}>
        Error: {error}
      </div>
    );
  }

  const filtersActive = filterState || filterClient || filterConsultantFirm || filterUrgency !== 'all';

  return (
    <div style={{ padding: '24px 32px', fontFamily: "'Public Sans',system-ui,sans-serif", color: text, background: bg, minHeight: '100%' }}>
      {/* ── Header ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: text, letterSpacing: '-0.01em' }}>
            Deadline Calendar
          </div>
          <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
            {filtered.length} of {events.length} parcel deadlines shown
            {!showCompleted && doneCount > 0 && ` · ${doneCount} completed hidden`}
            {filtered.length === 0 && events.length > 0 && ' · adjust filters to see results'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, background: panelBg, border: `1px solid ${border}`, borderRadius: 8, padding: 3 }}>
          {['list', 'month'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: view === v ? 700 : 500,
                background: view === v ? accent : 'transparent',
                color: view === v ? 'var(--ink)' : text,
                border: 'none', borderRadius: 6, cursor: 'pointer', textTransform: 'capitalize',
              }}>{v}</button>
          ))}
        </div>
      </div>

      {/* ── Filter strip ──────────────────────────────────────────── */}
      <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: 14, marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterSelect label="State" value={filterState} onChange={setFilterState}
          options={[{ value: '', label: 'All states' }, ...uniqueStates.map(s => ({ value: s, label: s }))]}
          {...{ isDark, border, text, muted }} />
        <FilterSelect label="Client" value={filterClient} onChange={setFilterClient}
          options={[{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: c.short_name || c.name || c.id }))]}
          {...{ isDark, border, text, muted }} />
        <FilterSelect label="Consultant" value={filterConsultantFirm} onChange={setFilterConsultantFirm}
          options={[{ value: '', label: 'All consultants' }, ...consultantFirms.map(f => ({ value: f, label: f }))]}
          {...{ isDark, border, text, muted }} />
        <FilterSelect label="Window" value={filterUrgency} onChange={setFilterUrgency}
          options={URGENCY_WINDOWS.map(u => ({ value: u.id, label: u.label }))}
          {...{ isDark, border, text, muted }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: text, cursor: 'pointer', alignSelf: 'flex-end', paddingBottom: 6 }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Show completed{doneCount > 0 ? ` (${doneCount})` : ''}
        </label>
        {filtersActive && (
          <button onClick={() => {
            setFilterState(''); setFilterClient(''); setFilterConsultantFirm(''); setFilterUrgency('all');
          }} style={{ padding: '6px 12px', fontSize: 11, color: muted, background: 'transparent', border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer' }}>
            Clear filters
          </button>
        )}
      </div>

      {/* ── Urgency legend ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, fontSize: 11, marginBottom: 18, flexWrap: 'wrap' }}>
        <LegendChip color={C.missed} label="Past due" />
        <LegendChip color={C.today} label="Today" />
        <LegendChip color={C.on_us} label="Within 7 days" />
        <LegendChip color={C.coming_up} label="8-30 days" />
        <LegendChip color={C.far_out} label="30+ days" />
        {showCompleted && <LegendChip color={DONE} label="Completed" />}
      </div>

      {/* ── List view ─────────────────────────────────────────────── */}
      {view === 'list' && (
        <ListView sorted={sorted} sortKey={sortKey} sortDir={sortDir}
          setSortKey={setSortKey} setSortDir={setSortDir}
          clientById={clientById}
          C={C} DONE={DONE} panelBg={panelBg} border={border} text={text} muted={muted} accent={accent} />
      )}

      {/* ── Month grid view ──────────────────────────────────────── */}
      {view === 'month' && (
        <MonthView events={filtered} monthOffset={monthOffset} setMonthOffset={setMonthOffset}
          clientById={clientById} C={C} DONE={DONE} panelBg={panelBg} border={border}
          text={text} muted={muted} accent={accent} />
      )}
    </div>
  );
}

// ── Filter dropdown component ────────────────────────────────────────
function FilterSelect({ label, value, onChange, options, isDark, border, text, muted }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ fontSize: 10, color: muted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
        {label}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{
          padding: '6px 10px', fontSize: 12, fontFamily: 'inherit',
          background: isDark ? 'var(--ink)' : '#f8f9fc', color: text,
          border: `1px solid ${border}`, borderRadius: 6, minWidth: 140, cursor: 'pointer',
        }}>
        {options.map((opt, i) => (
          <option key={`${opt.value}-${i}`} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── Legend chip ──────────────────────────────────────────────────────
function LegendChip({ color, label }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-block', width: 12, height: 12, borderRadius: 3,
        background: color.bg, border: `1px solid ${color.border}`,
      }} />
      <span style={{ color: color.fg, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

// ── List view component ──────────────────────────────────────────────
function ListView({ sorted, sortKey, sortDir, setSortKey, setSortDir, clientById, C, DONE, panelBg, border, text, muted, accent }) {
  const headerStyle = {
    padding: '10px 12px', fontSize: 11, fontWeight: 700, textAlign: 'left',
    color: muted, textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: `1px solid ${border}`, cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap',
  };
  const cellStyle = {
    padding: '12px', fontSize: 13, color: text, borderBottom: `1px solid ${border}`,
    verticalAlign: 'top',
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4, color: accent }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (!sorted.length) {
    return (
      <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: 40, textAlign: 'center', color: muted, fontSize: 13 }}>
        No deadlines match the current filters.
      </div>
    );
  }

  return (
    <ScrollX style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headerStyle} onClick={() => toggleSort('deadline')}>Deadline<SortIndicator k="deadline" /></th>
            <th style={headerStyle}>Urgency</th>
            <th style={headerStyle}>What</th>
            <th style={headerStyle} onClick={() => toggleSort('state')}>State<SortIndicator k="state" /></th>
            <th style={headerStyle} onClick={() => toggleSort('account_number')}>Account #<SortIndicator k="account_number" /></th>
            <th style={headerStyle}>Address</th>
            <th style={headerStyle} onClick={() => toggleSort('client')}>Client<SortIndicator k="client" /></th>
            <th style={headerStyle} onClick={() => toggleSort('consultant')}>Consultant<SortIndicator k="consultant" /></th>
            <th style={headerStyle} onClick={() => toggleSort('notice_date')}>Notice<SortIndicator k="notice_date" /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e, idx) => {
            const u = e.done ? null : urgencyTier(e.deadline);
            const color = e.done ? DONE : (u ? C[u.tier] : C.far_out);
            const client = clientById.get(e.client_id);
            const clientName = client?.short_name || client?.name || e.client_name_from_parcel || e.client_id || '—';
            // A done row is greyed whole, not just its badge.
            const rowCell = e.done ? { ...cellStyle, color: DONE.fg } : cellStyle;
            return (
              <tr key={`${e.parcel_id}-${idx}`}>
                <td style={{ ...rowCell, fontWeight: 600, whiteSpace: 'nowrap' }}
                  title={`${e.year} · dates from the ${e.date_source}`}>{formatDate(e.deadline)}</td>
                <td style={rowCell}>
                  <span style={{
                    display: 'inline-block', padding: '3px 8px', fontSize: 11, fontWeight: 700,
                    background: color.bg, color: color.fg, border: `1px solid ${color.border}`,
                    borderRadius: 4, whiteSpace: 'nowrap',
                  }}>{e.done || (u ? u.label : '—')}</span>
                </td>
                <td style={{ ...rowCell, fontSize: 12, whiteSpace: 'nowrap' }}>{e.what}</td>
                <td style={rowCell}>{e.state || '—'}</td>
                <td style={{ ...rowCell, fontFamily: 'monospace', fontSize: 12 }}>{e.account_number || '—'}</td>
                <td style={{ ...rowCell, color: muted, fontSize: 12, maxWidth: 280 }}>
                  {e.address || e.parcel_name || '—'}
                  {e.city && <div style={{ fontSize: 11, color: muted, opacity: 0.7 }}>{e.city}</div>}
                </td>
                <td style={rowCell}>{clientName}</td>
                <td style={{ ...rowCell, color: muted, fontSize: 12 }}>{e.consultant || '—'}</td>
                <td style={{ ...rowCell, color: muted, whiteSpace: 'nowrap' }}>{formatDate(e.notice_date)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollX>
  );
}

// ── Month grid view ──────────────────────────────────────────────────
function MonthView({ events, monthOffset, setMonthOffset, clientById, C, DONE, panelBg, border, text, muted, accent }) {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = target.getFullYear();
  const month = target.getMonth();

  // Index events by their deadline date string
  const eventsByDate = useMemo(() => {
    const m = new Map();
    events.forEach(e => {
      if (!m.has(e.deadline)) m.set(e.deadline, []);
      m.get(e.deadline).push(e);
    });
    return m;
  }, [events]);

  // Build grid
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const todayIso = todayLocalIso();

  return (
    <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: `1px solid ${border}` }}>
        <button onClick={() => setMonthOffset(monthOffset - 1)}
          style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: text, border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer' }}>← Prev</button>
        <div style={{ fontSize: 16, fontWeight: 700, color: text }}>
          {MONTH_NAMES[month]} {year}
        </div>
        <button onClick={() => setMonthOffset(monthOffset + 1)}
          style={{ padding: '6px 12px', fontSize: 12, background: 'transparent', color: text, border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer' }}>Next →</button>
      </div>
      {monthOffset !== 0 && (
        <div style={{ padding: '8px 18px', fontSize: 11, color: muted, borderBottom: `1px solid ${border}` }}>
          <button onClick={() => setMonthOffset(0)} style={{ background: 'transparent', border: 'none', color: accent, cursor: 'pointer', fontWeight: 600, fontSize: 11 }}>
            ← Back to current month
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${border}` }}>
        {DAY_HEADERS.map(d => (
          <div key={d} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((d, i) => {
          if (d === null) {
            return <div key={`empty-${i}`} style={{ minHeight: 100, borderRight: `1px solid ${border}`, borderBottom: `1px solid ${border}`, background: 'transparent' }} />;
          }
          const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const isToday = iso === todayIso;
          const dayEvents = eventsByDate.get(iso) || [];
          return (
            <div key={`day-${iso}`} style={{
              minHeight: 100,
              borderRight: `1px solid ${border}`,
              borderBottom: `1px solid ${border}`,
              padding: 6,
              background: isToday ? 'rgba(180,0,0,0.08)' : 'transparent',
              position: 'relative',
            }}>
              <div style={{
                fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? accent : text,
                marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              }}>
                <span>{d}</span>
                {dayEvents.length > 0 && (
                  <span style={{ fontSize: 10, color: muted, fontWeight: 500 }}>{dayEvents.length}</span>
                )}
              </div>
              {dayEvents.slice(0, 3).map((e, idx) => {
                const u = urgencyTier(e.deadline);
                const color = e.done ? DONE : (u ? C[u.tier] : C.far_out);
                const client = clientById.get(e.client_id);
                const clientLabel = client?.short_name || client?.name || e.client_name_from_parcel || e.account_number || 'parcel';
                return (
                  <div key={`${e.parcel_id}-${idx}`}
                    title={`${e.what}${e.done ? ` — ${e.done}` : ''}\n${e.account_number || '?'} — ${clientLabel} — ${e.state}${e.address ? '\n' + e.address : ''}`}
                    style={{
                      fontSize: 10, padding: '2px 5px', marginBottom: 2,
                      background: color.bg, color: color.fg,
                      borderLeft: `2px solid ${color.border}`, borderRadius: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                    {e.state}: {String(clientLabel).substring(0, 14)}
                  </div>
                );
              })}
              {dayEvents.length > 3 && (
                <div style={{ fontSize: 9, color: muted, marginTop: 2 }}>+{dayEvents.length - 3} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Date formatter (uses local-time YYYY-MM-DD without TZ shift) ─────
function formatDate(iso) {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const y = parseInt(m[1]);
  const mo = parseInt(m[2]) - 1;
  const d = parseInt(m[3]);
  return `${MONTH_ABBR[mo]} ${d}, ${y}`;
}
